/**
 * Modal for Content admin: Embed codes reference and other help guides.
 * Opened from the Help / Guide button on CMS Pages, Navigation, Header/Footer, etc.
 */

import { X } from 'lucide-react';
import { AdminCmsPlaceholderGuide } from './AdminCmsPlaceholderGuide';

interface AdminContentHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AdminContentHelpModal({
  isOpen,
  onClose,
}: AdminContentHelpModalProps): JSX.Element | null {
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="content-help-modal-title"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 px-5 py-4 border-b border-gray-200 shrink-0">
          <h2
            id="content-help-modal-title"
            className="text-lg font-semibold text-gray-900"
          >
            Content help &amp; guides
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Close help"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto px-5 py-4 space-y-6">
          {/* Embed codes for CMS content */}
          <section aria-labelledby="embed-codes-heading">
            <AdminCmsPlaceholderGuide
              title="Embed codes for CMS content"
              context="Use these in CMS page body, navigation HTML, or custom header/footer; they are replaced with values from Settings & Branding."
            />
          </section>

          {/* Placeholder for future guides / tutorials */}
          <section aria-labelledby="more-help-heading">
            <h3
              id="more-help-heading"
              className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2"
            >
              More help
            </h3>
            <p className="text-sm text-gray-500">
              Additional guides and tutorials can be added here. Check back for
              tips on editing pages, managing navigation, and using the CMS.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
